/**
 * Honest microphone detection.
 *
 * WHY THIS EXISTS. On 2026-08-02 a teacher abandoned a live 1:1 class to another calling app after
 * 19 minutes with "Audio problem - nobody can hear". Nothing in the product could
 * tell anyone a microphone was unavailable: the prejoin only ever acquired a
 * CAMERA track (and only when the camera was on, which is off by default on a
 * rejoin), so the mic toggle was decoration — it said "on" while the browser gave
 * us nothing. The teacher saw a plain mute icon, indistinguishable from a child who
 * had chosen to mute.
 *
 * TWO STATE MACHINES, deliberately separate:
 *
 *   micAccess   can the browser give us a live capture AT ALL?
 *   micPublish  are we sending unmuted audio into the room?
 *
 * Collapsing them is the original bug. `isMicrophoneEnabled === false` means
 * "chose to mute" OR "browser is blocking" OR "no microphone exists", and a teacher
 * needs to act differently in each case.
 *
 * getUserMedia is the AUTHORITY. The Permissions API is a hint only: it cannot be
 * queried for the microphone on some browsers, it goes stale when the user
 * changes a setting in another tab, and it can report `granted` while capture
 * fails because the device vanished or is held by another app.
 *
 * WHAT THIS DELIBERATELY DOES NOT CLAIM. `ok` does not mean "anyone can hear
 * you". OS-level mute, a hardware mute switch, a muted headset inline control, or
 * the wrong input device all produce a perfectly live track of near-silence. That
 * is why `measureLevel` exists and why the prejoin asks the child to speak: a
 * PROMPTED level check is reliable, whereas passively flagging quiet as broken
 * would punish shy children all day.
 */

/**
 * Coarse on purpose — coachable buckets, not a forensic report.
 *
 * `silent` is the odd one out and the most Windows-shaped: the browser handed us
 * a perfectly live track that carries nothing but silence. No getUserMedia error
 * exists for it, so it can never come out of `classify` below — it is asserted
 * from evidence instead (livekit's own post-publish `checkForSilence`, surfaced
 * via `RoomEvent.LocalAudioSilenceDetected`). Its causes are all things a person
 * can fix and none of them are permissions: Windows Settings → Privacy →
 * Microphone off for desktop apps, the wrong Realtek endpoint selected while
 * "Default" and "Communications" disagree, an inline mute switch on a headset
 * cable, or an exclusive-mode application holding the real device while a silent
 * virtual one is handed to the browser.
 *
 * It is deliberately distinct from `ok`: "we have a microphone" and "you can be
 * heard" are different claims, and collapsing them is the 2026-08-02 incident.
 */
export type DeviceAccess = 'ok' | 'blocked' | 'missing' | 'busy' | 'silent' | 'unknown'
/** Kept as an alias: the microphone was the first caller and reads better at use sites. */
export type MicAccess = DeviceAccess

export interface MicProbeResult {
  access: DeviceAccess
  /** The DOMException name, for telemetry only. Never shown to a user. */
  errorName?: string
  /** Kept open so the caller can meter it; the caller MUST stop it. */
  stream?: MediaStream
}

/**
 * Every one of these means "the browser refused or could not deliver capture".
 * The mapping is coarse on purpose: `NotAllowedError` covers a site-level block,
 * a dismissed prompt, an Android app-level permission and an enterprise policy,
 * and no API distinguishes them. Claiming to know which would be a lie that
 * teaches teachers to distrust the UI.
 */
export function classifyDeviceError(error: unknown): { access: DeviceAccess; errorName?: string } {
  return classify(error)
}

function classify(error: unknown): { access: DeviceAccess; errorName?: string } {
  const name = (error as { name?: string } | undefined)?.name
  switch (name) {
    case 'NotAllowedError':
    case 'PermissionDeniedError':
    case 'SecurityError':
      return { access: 'blocked', errorName: name }
    case 'NotFoundError':
    case 'DevicesNotFoundError':
      return { access: 'missing', errorName: name }
    case 'NotReadableError':
    case 'TrackStartError':
      return { access: 'busy', errorName: name }
    default:
      return { access: 'unknown', errorName: name }
  }
}

/**
 * Ask for a microphone and report what happened.
 *
 * `OverconstrainedError` retries bare, because a saved deviceId that no longer
 * exists (a Bluetooth headset that went to sleep between lessons) is a constraint
 * failure, not a missing microphone — and reporting "no microphone" to a child
 * holding a microphone is exactly the kind of confident wrongness this module is
 * meant to avoid.
 */
export async function probeMic(deviceId?: string): Promise<MicProbeResult> {
  const media = navigator.mediaDevices
  if (!media?.getUserMedia) return { access: 'unknown', errorName: 'unsupported' }

  const request = (constraint: MediaStreamConstraints) => media.getUserMedia(constraint)

  try {
    const stream = await request({ audio: deviceId ? { deviceId: { exact: deviceId } } : true })
    return { access: 'ok', stream }
  } catch (error) {
    const name = (error as { name?: string } | undefined)?.name
    if (name === 'OverconstrainedError' && deviceId) {
      try {
        const stream = await request({ audio: true })
        return { access: 'ok', stream }
      } catch (retryError) {
        return classify(retryError)
      }
    }
    return classify(error)
  }
}

/**
 * Same question for the camera.
 *
 * Separate call from the microphone ON PURPOSE. A combined
 * `getUserMedia({ audio: true, video: true })` fails as a single error, so one
 * missing device makes both look broken and the telemetry cannot say WHICH — the
 * exact ambiguity that left tonight's second incident resting on the teacher's
 * guess rather than on evidence. Two calls cost one extra permission prompt at
 * worst and buy an unambiguous answer.
 *
 * `missing` here is a NORMAL state, not a failure: plenty of desktops have no
 * webcam, and a lesson with a working microphone is a perfectly good lesson.
 * Callers must treat it as "continue without video", never as a blocked join.
 */
export async function probeCamera(deviceId?: string): Promise<MicProbeResult> {
  const media = navigator.mediaDevices
  if (!media?.getUserMedia) return { access: 'unknown', errorName: 'unsupported' }

  try {
    const stream = await media.getUserMedia({
      video: deviceId ? { deviceId: { exact: deviceId } } : true,
    })
    return { access: 'ok', stream }
  } catch (error) {
    const name = (error as { name?: string } | undefined)?.name
    if (name === 'OverconstrainedError' && deviceId) {
      try {
        const stream = await media.getUserMedia({ video: true })
        return { access: 'ok', stream }
      } catch (retryError) {
        return classify(retryError)
      }
    }
    return classify(error)
  }
}

/** Release a probe stream. Holding it open is how you cause your own `busy`. */
export function stopStream(stream: MediaStream | undefined): void {
  stream?.getTracks().forEach((track) => track.stop())
}

export interface LevelMeter {
  /** 0..1, smoothed. Read it on a frame loop. */
  level: () => number
  stop: () => void
}

/**
 * Live input level off a probe stream, so the prejoin can ask a child to say
 * hello and SHOW them they are being heard.
 *
 * This is the only check that catches the failure the incident may actually have
 * been: permission granted, capture live, and still nothing audible because the
 * headset's inline switch is muted or the OS input is the wrong device. A
 * permission gate alone returns `ok` for all of those.
 *
 * Returns null where Web Audio is unavailable — the caller then simply does not
 * offer the check rather than pretending it passed.
 */
export function createLevelMeter(stream: MediaStream): LevelMeter | null {
  const Ctor: typeof AudioContext | undefined =
    window.AudioContext ??
    (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
  if (!Ctor) return null

  let context: AudioContext
  try {
    context = new Ctor()
  } catch {
    return null
  }

  // Chrome starts an AudioContext SUSPENDED until a user gesture, and a restored
  // tab or a phone handed over mid-screen may not have had one. A suspended
  // context reports a flat zero, so the meter would sit still while a perfectly
  // good microphone was capturing — and we would tell a child with working audio
  // that we cannot hear them, which is worse than saying nothing.
  void context.resume?.().catch(() => undefined)

  const analyser = context.createAnalyser()
  // Small FFT: we want a loudness number, not a spectrum, and this runs on the
  // weakest phones in the cohort.
  analyser.fftSize = 512
  analyser.smoothingTimeConstant = 0.6
  const source = context.createMediaStreamSource(stream)
  source.connect(analyser)

  const buffer = new Uint8Array(analyser.frequencyBinCount)
  let smoothed = 0

  return {
    level: () => {
      analyser.getByteFrequencyData(buffer)
      let sum = 0
      for (const value of buffer) sum += value * value
      // RMS over the band, normalised. Squared then rooted so a loud narrow peak
      // (a tap on the desk) does not read the same as speech.
      const rms = Math.sqrt(sum / buffer.length) / 255
      // Attack fast, release slow: the meter should jump when a child speaks and
      // linger long enough for them to see that it moved.
      smoothed = rms > smoothed ? rms : smoothed * 0.88 + rms * 0.12
      return Math.min(1, smoothed * 2.2)
    },
    stop: () => {
      try {
        source.disconnect()
        void context.close()
      } catch {
        /* already torn down */
      }
    },
  }
}

/**
 * Whether a state should stop a student who expects to be heard.
 * `unknown` deliberately does NOT block: a probe that failed for a reason we do
 * not understand must never be the thing that keeps a child out of their lesson.
 */
export function needsHelp(access: MicAccess): boolean {
  return access === 'blocked' || access === 'missing' || access === 'busy' || access === 'silent'
}

/**
 * The last verdict this tab reached, so the room can advertise it.
 *
 * A module-scoped holder rather than another prop threaded through the phase
 * machine: the probe happens on the prejoin screen and is needed inside
 * <LiveKitRoom>, and LocalUserChoices (the only thing that crosses that
 * boundary) is a LiveKit type we do not own. Deliberately NOT persisted — a
 * verdict is only true for this device, this tab, right now, and a stale "blocked"
 * restored from storage would tell a teacher something false.
 */
let lastMicAccess: DeviceAccess | null = null
let lastCamAccess: DeviceAccess | null = null

export function setLastMicAccess(access: DeviceAccess): void {
  lastMicAccess = access
}

export function getLastMicAccess(): DeviceAccess | null {
  return lastMicAccess
}

export function setLastCamAccess(access: DeviceAccess): void {
  lastCamAccess = access
}

export function getLastCamAccess(): DeviceAccess | null {
  return lastCamAccess
}

/**
 * How long a verdict may be presented to a teacher as current.
 *
 * A device state is a claim about a moment: a child can unplug a headset, a
 * parent can flip a permission, a Bluetooth mic can sleep. Showing a stale
 * "blocked" as fact is how a teacher is talked into a another calling app switch for a problem
 * that has already been fixed, so anything older than this reads as `unknown`
 * rather than as truth.
 */
export const ACCESS_FRESH_MS = 10 * 60 * 1000
